import { CompanyRole, LoadState, User, UserRoles } from '@diditusa/shared/types/commonTypes';
import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  ICognitoUserPoolData,
} from 'amazon-cognito-identity-js';
import {
  useExchangeCodeForTokenMutation,
  useHandleLoginInviteTokenMutation,
  useLazyLoadUserQuery,
} from '@diditusa/shared/api/sessionApi';
import { useAppDispatch, useAppSelector } from '../hooks';
import { setAuthState, updateUserFromCachedSession } from '@diditusa/shared/reducers/SessionSlice';
import { useGetConfigQuery } from '@diditusa/shared/api/configApi';
import { AuthState } from '@diditusa/shared/types/authTypes';

type AuthContextType = {
  isAuthenticated: boolean;
  loadState: LoadState;
  user: User | null;
  logout: () => void;
  login: () => void;
  signUp: () => void;
  userRole: UserRoles;
};

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  loadState: LoadState.INIT,
  user: null,
  logout: () => {},
  login: () => {},
  signUp: () => {},
  userRole: UserRoles.USER,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { user: userFromStore } = useAppSelector((state) => state.root.sessionState);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>(LoadState.INIT);
  const [user, setUser] = useState<User | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const authCode = new URLSearchParams(location.search).get('code');
  const stateParam = new URLSearchParams(location.search).get('state');
  const [handleLoginInviteToken, handleLoginInviteTokenState] = useHandleLoginInviteTokenMutation();
  const [exchangeCodeForToken, exchangeCodeForTokenState] = useExchangeCodeForTokenMutation();
  const { data: config, isLoading: isConfigLoading, isError: isConfigError } = useGetConfigQuery();
  const userPoolRef = useRef<CognitoUserPool>(null);
  const userRef = useRef<CognitoUser>(null);
  const [loadUserFromServer, { data: userFromServer, ...loadUserState }] = useLazyLoadUserQuery();
  const [userRole, setUserRole] = useState<UserRoles>(UserRoles.USER);

  // eslint-disable-next-line
  useEffect(() => {
    // Once config is loaded then create the user pool and
    // trigger the session load
    if (isConfigLoading) return;

    if (isConfigError) {
      console.error('Error loading config');
      return;
    }

    const poolData: ICognitoUserPoolData = {
      UserPoolId: config?.userPoolId || '',
      ClientId: config?.userPoolClientId || '',
    };

    // if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
    //   poolData.endpoint = process.env.REACT_APP_COGNITO_ENDPOINT;
    // }
    userPoolRef.current = new CognitoUserPool(poolData);

    loadSession();
    // eslint-disable-next-line
  }, [config, isConfigError, isConfigLoading]);

  useEffect(() => {
    // If the session loads correctly then the user should be updated in the store.
    // We need to wait until the redux store is updated with the user
    // so the api call will have the tokens automatically added.
    if (userFromStore.idToken) {
      setUserRefSession(
        userFromStore.accessToken!,
        userFromStore.idToken,
        userFromStore.refreshToken || '',
      );
      loadUserFromServer();
    }
    // eslint-disable-next-line
  }, [userFromStore]);

  useEffect(() => {
    // We loaded the user from the backend (vs from the token).
    if (userFromServer) {
      setUser(userFromServer);
      setIsAuthenticated(true);
      setLoadState(LoadState.LOADED);
      if (userFromServer.company) {
        setUserRole(
          userFromServer.company.companyRole === CompanyRole.MANAGER
            ? UserRoles.MANAGER
            : UserRoles.EMPLOYEE,
        );
      }

      if (stateParam) {
        handleLoginInviteToken(stateParam);
      }

      cleanUpQueryParams();
    }

    if (loadUserState.isError) {
      setIsAuthenticated(true);
      setLoadState(LoadState.ERROR);
    }
    // eslint-disable-next-line
  }, [userFromServer, loadUserState.isError]);

  useEffect(() => {
    // Handle the result of the code exchange
    if (exchangeCodeForTokenState.isError) {
      setIsAuthenticated(false);
      setLoadState(LoadState.LOADED);
      return;
    }

    // if (exchangeCodeForTokenState.data) {
    //   onSuccessfulCodeExchange();
    // }
  }, [exchangeCodeForTokenState]);

  useEffect(() => {
    // Handle the result of the invite token
    if (handleLoginInviteTokenState.isError) {
      console.log((handleLoginInviteTokenState.error as any).data.message);
    }
    // eslint-disable-next-line
  }, [handleLoginInviteTokenState.isError, handleLoginInviteTokenState.error]);

  // const onSuccessfulCodeExchange = async () => {
  //   if (!exchangeCodeForTokenState.data) {
  //     return;
  //   }
  //   // const { access_token, id_token, refresh_token } = exchangeCodeForTokenState.data;
  //   // setUserRefSession(access_token, id_token, refresh_token);
  //   if (stateParam) {
  //     handleLoginInviteToken(stateParam);
  //   }
  // };

  const setUserRefSession = (accessToken: string, idToken: string, refreshToken: string) => {
    const IdToken = new CognitoIdToken({
      IdToken: idToken,
    });
    const AccessToken = new CognitoAccessToken({
      AccessToken: accessToken,
    });
    const RefreshToken = new CognitoRefreshToken({ RefreshToken: refreshToken });
    const session = new CognitoUserSession({
      IdToken,
      AccessToken,
      RefreshToken,
    });

    userRef.current = new CognitoUser({
      Username: userFromStore.username!,
      Pool: userPoolRef.current!,
    });
    userRef.current.setSignInUserSession(session);
  };

  const getRedirectUri = () => {
    return `${window.location.origin}/home`;
  };

  const loadSession = () => {
    if (!userPoolRef.current) {
      console.error('UserPool undefined');
      return;
    }

    const userFromPool = userPoolRef.current.getCurrentUser();

    if (!userFromPool) {
      if (authCode && config) {
        // If user is null and we have auth code then we likely arrived here after being redirected
        // from the sign in hosted UI. Need to exchange code for tokens.
        const exchangeRequest = {
          clientId: config.userPoolClientId!,
          code: authCode,
          redirectUrl: getRedirectUri(),
        };
        exchangeCodeForToken(exchangeRequest);
        return;
      }

      setIsAuthenticated(false);
      setLoadState(LoadState.LOADED);
      return;
    }

    userFromPool.getSession((err: Error | null, session: CognitoUserSession) => {
      if (err) {
        console.log('Error getting session', err);
        userFromPool.signOut();
        setIsAuthenticated(false);
        setLoadState(LoadState.LOADED);
        dispatch(setAuthState(AuthState.UNAUTHENTICATED));
        return;
      }

      const exp = session.getAccessToken().getExpiration(); // UNIX timestamp (seconds)

      const now = Math.floor(Date.now() / 1000); // current time in seconds

      if (exp < now) {
        userFromPool.refreshSession(session.getRefreshToken(), (err, newSession) => {
          if (err) {
            console.error('Failed to refresh token ', err);
            dispatch(setAuthState(AuthState.UNAUTHENTICATED));
          } else {
            console.log('Session refreshed!');
            dispatch(updateUserFromCachedSession(newSession));
          }
        });
      } else {
        dispatch(updateUserFromCachedSession(session));
      }
    });
  };

  const logout = () => {
    userRef.current?.globalSignOut({
      onSuccess: (msg: string) => console.log('Success ', msg),
      onFailure: (err) => {
        console.error('Error signing out ', err);
        userRef.current?.signOut();
      },
    });
    setUser(null);
    setIsAuthenticated(false);
  };

  const login = () => {
    const loginUrl = `${config?.userLoginUrl}&redirect_uri=${getRedirectUri()}`;
    window.location.href = loginUrl;
  };
  const cleanUpQueryParams = () => {
    const params = new URLSearchParams(location.search);
    params.delete('code');
    params.delete('state');

    const newSearch = params.toString();

    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  };

  const signUp = () => {
    const loginUrl = `${config?.userLoginUrl}&redirect_uri=${getRedirectUri()}`;
    const signUpUrl = loginUrl.replace('/login', '/signup');
    window.location.href = signUpUrl;
  };
  return (
    <AuthContext.Provider
      value={{ isAuthenticated, loadState, user, logout, login, signUp, userRole }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
